import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, FileText, Clock, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EngineerReportsPage = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, approved, rejected
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/progress-reports/all-reviewed`)
      .then(res => setReports(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? reports : reports.filter(r => r.status === filter);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto" data-testid="engineer-reports-page">
        <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{t("engineerReports.title")}</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">{t("engineerReports.subtitle")}</p>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-5">
          {[
            { key: "all", label: `${t("engineerReports.all")} (${reports.length})` },
            { key: "approved", label: `${t("engineerReports.approved")} (${reports.filter(r => r.status === "approved").length})` },
            { key: "rejected", label: `${t("engineerReports.rejected")} (${reports.filter(r => r.status === "rejected").length})` },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setFilter(tab.key)}
              data-testid={`filter-${tab.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                filter === tab.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-12">{t("engineerReports.loading")}</p>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="text-center py-12 text-sm text-slate-400">{t("engineerReports.noReports")}</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(report => (
              <Card key={report.id} className="border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`report-card-${report.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {report.status === "approved" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <h3 className="font-semibold text-slate-900 text-sm truncate">{report.title}</h3>
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          report.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                        }`}>
                          {report.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
                        <span>{report.partner_name}</span>
                        {report.project_name && <span>{report.project_name}</span>}
                        {report.milestone_label && <span>{report.milestone_label}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-400">
                        <span>Submitted: {new Date(report.created_at).toLocaleDateString()}</span>
                        {report.reviewed_at && (
                          <span className="font-medium text-slate-600">
                            {t("engineerReports.reviewed")}: {new Date(report.reviewed_at).toLocaleDateString()}
                          </span>
                        )}
                        {report.reviewer_name && <span>{t("engineerReports.by")} {report.reviewer_name}</span>}
                      </div>

                      {/* Material info */}
                      {report.material_total > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                          <span className="text-xs font-semibold text-blue-700">
                            {report.material_total} {report.material_unit_of_measure || "m\u00B2"}
                          </span>
                          {report.engineer_adjusted_quantities && (
                            <span className="text-[10px] text-amber-600">{t("engineerReports.adjustedByEngineer")}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Expand button */}
                    <button onClick={() => setExpanded(expanded === report.id ? null : report.id)}
                      className="text-slate-400 hover:text-slate-600 p-1" data-testid={`expand-${report.id}`}>
                      {expanded === report.id ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                    </button>
                  </div>

                  {/* Expanded details */}
                  {expanded === report.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                      {report.description && (
                        <p className="text-xs text-slate-600">{report.description}</p>
                      )}
                      {report.review_notes && (
                        <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase">{t("engineerReports.reviewNotes")}</p>
                          <p className="text-xs text-slate-700 mt-0.5">{report.review_notes}</p>
                        </div>
                      )}
                      {report.engineer_quantity_notes && (
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-2">
                          <p className="text-[10px] font-semibold text-amber-600 uppercase">{t("engineerReports.quantityAdjustment")}</p>
                          <p className="text-xs text-amber-800 mt-0.5">{report.engineer_quantity_notes}</p>
                        </div>
                      )}
                      {report.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {report.attachments.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5">
                              <FileText className="w-3 h-3" /> {t("engineerReports.doc")} {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EngineerReportsPage;
